import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui-kit/button';
import {
  Dialog,
  DialogCancelButton,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import { Input } from '@/components/ui-kit/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui-kit/form';
import { useCreateCtcLevel, useUpdateCtcLevel } from '../../hooks/use-ctc-levels';
import { ctcLevelFormSchema, CtcLevelFormValues } from '../../schemas/ctc-level-form-schema';
import { CtcLevel } from '../../types/ctc-level.types';

export interface CtcLevelFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ctcLevel?: CtcLevel | null;
}

const EMPTY_VALUES: CtcLevelFormValues = {
  Level: '',
  CTC: '',
  Ranks: '',
};

export const CtcLevelFormDialog = ({ open, onOpenChange, ctcLevel }: CtcLevelFormDialogProps) => {
  const { t } = useTranslation();
  const isEditMode = Boolean(ctcLevel);

  const { mutateAsync: createCtcLevel, isPending: isCreating } = useCreateCtcLevel();
  const { mutateAsync: updateCtcLevel, isPending: isUpdating } = useUpdateCtcLevel();
  const isSubmitting = isCreating || isUpdating;

  const form = useForm<CtcLevelFormValues>({
    resolver: async (data, context, options) => {
      const result = await zodResolver(ctcLevelFormSchema)(data, context, options);
      if (result.errors) {
        result.errors = Object.fromEntries(
          Object.entries(result.errors).map(([key, error]) => [
            key,
            { ...error, message: t((error as { message?: string }).message ?? '') },
          ])
        );
      }
      return result;
    },
    defaultValues: EMPTY_VALUES,
  });

  useEffect(() => {
    if (!open) return;
    form.reset({
      Level: ctcLevel?.Level ?? '',
      CTC: ctcLevel?.CTC != null ? String(ctcLevel.CTC) : '',
      Ranks: ctcLevel?.Ranks ?? '',
    });
  }, [open, ctcLevel, form]);

  const handleSubmit = async (values: CtcLevelFormValues) => {
    const input = {
      Level: values.Level,
      CTC: Number(values.CTC),
      Ranks: values.Ranks ?? '',
    };
    try {
      if (isEditMode && ctcLevel) {
        await updateCtcLevel({ itemId: ctcLevel.ItemId, input });
      } else {
        await createCtcLevel(input);
      }
      onOpenChange(false);
    } catch {
      // Error toast is already shown by the mutation's global error handler.
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>{isEditMode ? t('EDIT_CTC_LEVEL') : t('CREATE_CTC_LEVEL')}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="Level"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('CTC_LEVEL')} *</FormLabel>
                  <FormControl>
                    <Input placeholder={t('CTC_LEVEL')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="CTC"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('CTC_AMOUNT')} *</FormLabel>
                  <FormControl>
                    <Input
                      type="text"
                      inputMode="numeric"
                      placeholder={t('CTC_AMOUNT')}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="Ranks"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('CTC_RANKS')}</FormLabel>
                  <FormControl>
                    <Input placeholder={t('CTC_RANKS')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <DialogCancelButton onClick={() => onOpenChange(false)}>
                {t('CANCEL')}
              </DialogCancelButton>
              <Button type="submit" loading={isSubmitting} disabled={isSubmitting}>
                {isEditMode ? t('SAVE_CHANGES') : t('CREATE')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
